import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  PhotoLibrary as PhotoIcon,
  AutoFixHigh as TransformIcon,
  Event as EventIcon,
  Flight as TripIcon,
  Person as ProfileIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import { collection, query, where, getDocs, getFirestore } from 'firebase/firestore';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

interface PhotoMetrics {
  totalPhotos: number;
  eventPhotos: number;
  tripPhotos: number;
  profilePhotos: number;
  transformedPhotos: number;
  sharedTransformations: number;
  photosBySource: { name: string; value: number }[];
  transformationsByStyle: { name: string; value: number }[];
  loading: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AnalyticsPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState<PhotoMetrics>({
    totalPhotos: 0,
    eventPhotos: 0,
    tripPhotos: 0,
    profilePhotos: 0,
    transformedPhotos: 0,
    sharedTransformations: 0,
    photosBySource: [],
    transformationsByStyle: [],
    loading: true
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!currentUser) return;

      try {
        const db = getFirestore();
        
        // Get event photos count
        const eventPhotosQuery = query(
           collection(db, 'photos'),
           where('uploadedBy', '==', currentUser.uid),
           where('eventId', '!=', null)
        );
        const eventPhotosSnapshot = await getDocs(eventPhotosQuery);
        const eventPhotosCount = eventPhotosSnapshot.size;
        
        // Get trip photos count
        const tripPhotosQuery = query(
           collection(db, 'photos'),
           where('uploadedBy', '==', currentUser.uid),
           where('tripId', '!=', null)
        );
        const tripPhotosSnapshot = await getDocs(tripPhotosQuery);
        const tripPhotosCount = tripPhotosSnapshot.size;
        
        // Get profile photos count
        const profilePhotosQuery = query(
           collection(db, 'photos'),
           where('uploadedBy', '==', currentUser.uid),
           where('type', '==', 'user_photo')
        );
        const profilePhotosSnapshot = await getDocs(profilePhotosQuery);
        const profilePhotosCount = profilePhotosSnapshot.size;
        
        // Get transformed photos data
        const transformedPhotosQuery = query(
          collection(db, 'imageTransformations'),
          where('userId', '==', currentUser.uid)
        );
        const transformedPhotosSnapshot = await getDocs(transformedPhotosQuery);
        const transformedPhotosCount = transformedPhotosSnapshot.size;
        
        // Calculate shared transformations (mock data for now)
        const sharedCount = Math.round(transformedPhotosCount * 0.7);
        
        // Prepare chart data for photos by source
        const photosBySource = [
          { name: 'Events', value: eventPhotosCount },
          { name: 'Trips', value: tripPhotosCount },
          { name: 'Profile', value: profilePhotosCount }
        ];
        
        // Process transformation styles (mock data for now)
        const stylesData = [
          { name: 'Anime', value: Math.floor(Math.random() * 10) + 1 },
          { name: 'Digital Art', value: Math.floor(Math.random() * 15) + 1 },
          { name: 'Enhance', value: Math.floor(Math.random() * 20) + 5 },
          { name: 'Fantasy', value: Math.floor(Math.random() * 8) + 1 },
          { name: 'Photographic', value: Math.floor(Math.random() * 12) + 3 }
        ];
        
        setMetrics({
          totalPhotos: eventPhotosCount + tripPhotosCount + profilePhotosCount,
          eventPhotos: eventPhotosCount,
          tripPhotos: tripPhotosCount,
          profilePhotos: profilePhotosCount,
          transformedPhotos: transformedPhotosCount,
          sharedTransformations: sharedCount,
          photosBySource,
          transformationsByStyle: stylesData,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setMetrics(prev => ({ ...prev, loading: false }));
      }
    };

    fetchMetrics();
  }, [currentUser]);

  if (metrics.loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading analytics data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhotoIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Photos</Typography>
              </Box>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {metrics.totalPhotos}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip icon={<EventIcon />} label={`Events: ${metrics.eventPhotos}`} color="primary" size="small" />
                  <Chip icon={<TripIcon />} label={`Trips: ${metrics.tripPhotos}`} color="secondary" size="small" />
                </Stack>
                <Chip icon={<ProfileIcon />} label={`Profile: ${metrics.profilePhotos}`} color="info" size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TransformIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Transformations</Typography>
              </Box>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {metrics.transformedPhotos}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Transformation Rate
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={metrics.totalPhotos > 0 ? (metrics.transformedPhotos / metrics.totalPhotos) * 100 : 0} 
                  color="secondary"
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {metrics.totalPhotos > 0 
                    ? `${((metrics.transformedPhotos / metrics.totalPhotos) * 100).toFixed(1)}% of your photos transformed`
                    : 'No photos uploaded yet'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShareIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Social Sharing</Typography>
              </Box>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {metrics.sharedTransformations}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Share Rate
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={metrics.transformedPhotos > 0 ? (metrics.sharedTransformations / metrics.transformedPhotos) * 100 : 0} 
                  color="info"
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {metrics.transformedPhotos > 0 
                    ? `${((metrics.sharedTransformations / metrics.transformedPhotos) * 100).toFixed(1)}% of transformations shared`
                    : 'No transformations yet'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Photos by Source</Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={metrics.photosBySource}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {metrics.photosBySource.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} photos`, 'Count']} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Transformations by Style</Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={metrics.transformationsByStyle}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" name="Count" fill="#8884d8">
                    {metrics.transformationsByStyle.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPanel;
